import {Component, ElementRef, Inject, Input, SimpleChanges, ViewChild} from '@angular/core';
import {ChartPoint, ElevationStats, Lookups, Waypoint, WaypointType} from "@model";
import {TranslocoService} from "@ngneat/transloco";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {RaceProfileModalComponent} from "../race-profile-modal/race-profile-modal.component";
import {waypointTypeIcons} from "../../model/waypoint-type-icons";

import * as d3 from 'd3';
import {getLanguageValue, raceLengthFormat} from "@utility";
import {DOCUMENT} from "@angular/common";

const GRAPH_WIDTH = 962;

const TOP_BAR_HEIGHT = 193;
const TOP_BAR_PADDING = 28
const PROFILE_HEIGHT = 223;
const PROFILE_PADDING = 50
const PROFILE_WIDTH = GRAPH_WIDTH - PROFILE_PADDING - PROFILE_PADDING
const BOTTOM_BAR_HEIGHT = 126;
const TOP_BAR_POSITION_TOP = 0;
const TOP_BAR_POSITION_BOTTOM = TOP_BAR_HEIGHT;
const PROFILE_POSITION_TOP = TOP_BAR_POSITION_BOTTOM;
const DISTANCE_GROUP_PADDING_TOP = 9;
const NAME_GROUP_PADDING_TOP = 40;
const DISTANCE_AXIS_LABEL_PADDING_TOP = 68
const MARKER_RADIUS = 20


const PROFILE_POSITION_BOTTOM = PROFILE_POSITION_TOP + PROFILE_HEIGHT
const BOTTOM_BAR_POSITION_TOP = PROFILE_POSITION_BOTTOM;
const BOTTOM_BAR_POSTION_BOTTOM = BOTTOM_BAR_POSITION_TOP + BOTTOM_BAR_HEIGHT;
const AREA_TOOLTIP_ID = "elevation-tooltip"
const SERVICES_TOOLTIP_ID = "services-tooltip"
const DISTANCE_ID = "distance-"
const NAME_ID = "name-"
const LINE_ID = "line-"

const BASIC_CLASS = "race-profile "
const CP_LINE_CLASS = BASIC_CLASS + "w-5 color-primary dashed";
const CP_LINE_CLASS_ACTIVE = BASIC_CLASS + "w-75 color-primary"
const CP_NAME_LABEL_CLASS = BASIC_CLASS + "fs-14 fill-black fw-200 text-middle"
const CP_NAME_LABEL_CLASS_ACTIVE = BASIC_CLASS + "fs-16 fill-primary fw-600 text-middle"
const CP_MARKER_SERVICE_ICON = BASIC_CLASS + "";
const CP_MARKER_SERVICE_NUMBER = BASIC_CLASS + "fill-white pointer fs-16 text-middle fw-600"
const CP_DISTANCE_LABEL_CLASS = BASIC_CLASS + "fs-14 fill-black fw-100 text-middle"
const CP_MARKER_CIRCLE = BASIC_CLASS + "circle dashed";
const CIRCLE_TEXT = BASIC_CLASS + "circle-text"
const CP_DISTANCE_LABEL_CLASS_ACTIVE = BASIC_CLASS + "fs-16 fill-primary fw-200 text-middle"
const AXIS_LABEL_CLASS = BASIC_CLASS + "fs-16 fill-black fw-100";
const AXIS_LINE_CLASS = BASIC_CLASS + "w-5 color-black";
const SERVICES_TOOLTIP_BORDER = BASIC_CLASS + "shadow";
const HEIGHT_MARKER = BASIC_CLASS + "height-marker"

const SERVICES_TOOLTIP_CLASS = BASIC_CLASS + "fill-white"
const SERVICES_TOOLTIP_TEXT_CLASS = BASIC_CLASS + "fill-primary text-start"
const AREA_TOOLTIP_CLASS = BASIC_CLASS + "fill-grey"
const AREA_TOOLTIP_TEXT_CLASS = BASIC_CLASS + "fill-white text-start"
const AREA_HOVER_LINE_CLASS = BASIC_CLASS + "w-5 color-black"
const ELEVATION_LINE = BASIC_CLASS + " color-primary w-75 fill-none"


const GRAPH_HEIGHT = PROFILE_HEIGHT + TOP_BAR_HEIGHT + BOTTOM_BAR_HEIGHT;


@Component({
    selector: 'app-race-profile',
    templateUrl: './race-profile.component.html',
    styleUrl: './race-profile.component.scss',
    standalone: false
})
export class RaceProfileComponent {

  @Input({required: true}) languages: Lookups[] = [];
  @Input() defaultLanguage?: number | undefined;
  @Input() controlPointsList: Waypoint[] = [];
  @Input() gpx?: any;
  @Input() elevationStats: Map<number, ElevationStats> = new Map<number, ElevationStats>();

  points: ChartPoint[] = [];

  @ViewChild('raceProfile') svgElement?: ElementRef<SVGElement>;


  waypointTypeIcons: { [key: number]: string } = waypointTypeIcons;
  svg: any;


  constructor(private modalService: NgbModal, private translocoService: TranslocoService,
              @Inject(DOCUMENT) private document: Document) {
  }

  ngOnInit(): void {
    this.initPoints();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gpx']?.currentValue !== changes['gpx']?.previousValue) {
      this.initPoints();
    }
  }

  ngAfterViewInit() {
    this.drawGraph();
  }

  initPoints(): void {
    this.points = []
    if (this.gpx && this.gpx?.getElevationPoints()?.length > 0) {
      this.gpx!.getElevationPoints().forEach((t: [number, number]) => {
          this.points.push(new ChartPoint(t[0], t[1]))
        }
      );
    }
  }


  // Method to create SVG container and group
  createSvgAndGroup(svg: any) {
    return svg.append("svg")
      .attr('viewBox', '0 0 ' + GRAPH_WIDTH + ' ' + GRAPH_HEIGHT)
      .style('height', `${GRAPH_HEIGHT}px`)
      .style('display', 'block')
      .style('margin', 'auto')
      .attr('preserveAspectRatio', 'xMinYMid')
      .append('g');
  }

  createText(g: any, text: string[], className: string, x: number, y: number, textAlign: "start" | "end" = "start"): any {
    const textGroup = g.append("g")
    let dy = 0;
    text.forEach((t: string, index: number) => {
      const node = textGroup.append("text").attr("x", x).attr("y", y).attr("class", className).text(t);
      if (index === 0) {
        dy = node.node().getBoundingClientRect().height;
      }
      node.attr("dy", (dy * (index + 1)))
    })
    if (textAlign === "end") {
      const textWidth = textGroup.node().getBoundingClientRect().width;
      textGroup.selectAll("text").each(function (this: SVGGElement) {
        const textNode = d3.select(this);
        textNode.attr("dx", textWidth - (textNode.node()?.getBoundingClientRect().width ?? 0))
      });
    }

    return textGroup;
  }

  createToolTip(g: any, id: string, padding: number, rectX: number, rectY: number,
                position: "TOP_LEFT" | "TOP_RIGHT" | "BOTTOM_LEFT" | "BOTTOM_RIGHT", tooltipClass: string, textClass: string, text: string[], marginX: number, marginY: number): any {
    const group = g.append("g").attr("id", id);
    const el = this.createText(group, text, textClass, rectX, rectY);
    const width = el.node().getBoundingClientRect().width
    const height = el.node().getBoundingClientRect().height
    // Draw a rectangle around the text
    group.append("rect")
      .attr("x", rectX)
      .attr("y", rectY)
      .attr("width", width + 1)
      .attr("height", height + 1).attr("class", tooltipClass)
    group.append("rect").attr("height", padding + 1).attr("width", width).attr("x", rectX).attr("y", rectY - padding).attr("class", tooltipClass)
    group.append("rect").attr("height", padding + 1).attr("width", width).attr("x", rectX).attr("y", rectY + height - 1).attr("class", tooltipClass)
    group.append("rect").attr("height", height).attr("width", padding + 1).attr("x", rectX - padding).attr("y", rectY).attr("class", tooltipClass)
    group.append("rect").attr("height", height).attr("width", padding + 1).attr("x", rectX + width - 1).attr("y", rectY).attr("class", tooltipClass)
    let circlePositions: { x: number, y: number }[] = [];
    let cornerPosition: { x: number, y: number } = {x: rectX, y: rectY};

    if (position === "TOP_LEFT") {
      cornerPosition = {x: rectX + width - 1, y: rectY + height - 1};
      circlePositions = [{x: rectX, y: rectY}, {x: rectX + width, y: rectY}, {x: rectX, y: rectY + height}]
      group.attr("transform", `translate(${-(width + marginX)},${-(height + marginY)})`)
    }

    if (position === "BOTTOM_LEFT") {
      cornerPosition = {x: rectX + width - 1, y: rectY - padding};
      circlePositions = [{x: rectX, y: rectY}, {x: rectX, y: rectY + height}, {x: rectX + width, y: rectY + height}]
      group.attr("transform", `translate(${-(width + marginX)}, ${marginY})`)
    }

    if (position === "TOP_RIGHT") {
      cornerPosition = {x: rectX - padding, y: rectY + height - 1};
      circlePositions = [{x: rectX, y: rectY}, {x: rectX + width, y: rectY}, {x: rectX + width, y: rectY + height}]
      group.attr("transform", `translate(${marginX},${-(height + marginY)})`)
    }

    if (position === "BOTTOM_RIGHT") {
      cornerPosition = {x: rectX - padding, y: rectY - padding};
      circlePositions = [{x: rectX, y: rectY + height}, {x: rectX + width, y: rectY}, {
        x: rectX + width,
        y: rectY + height
      }]
      group.attr("transform", `translate(${marginX},${marginY})`)
    }

    group.append("rect").attr("height", padding + 1).attr("width", padding + 1).attr("x", cornerPosition.x).attr("y", cornerPosition.y).attr("class", tooltipClass)
    circlePositions.forEach(cp => {
      group.append("circle")
        .attr("cx", cp.x)
        .attr("cy", cp.y)
        .attr("r", padding)
        .attr("class", tooltipClass)
    })
    this.createText(group, text, textClass, rectX, rectY, position === "BOTTOM_RIGHT" || position === "TOP_RIGHT" ? "start" : "end")
    group.attr("class", tooltipClass);
    return group;
  }

// Method to create the area path
  createAreaPath(g: any, scaleX: any, scaleY: any) {
    const area = d3.area<ChartPoint>()
      .x(p => scaleX(p.x))
      .y0(scaleY(this.gpx?.get_elevation_min()))
      .y1(p => scaleY(p.y));


    return g.append("path")
      .datum(this.points)
      .attr("fill", "white")
      .attr("d", area);

  }

  drawAxis(g: any, scaleX: any, scaleY: any) {
    const axisGroup = g.append("g")
    axisGroup.append("line")
      .attr("class", AXIS_LINE_CLASS)
      .attr("x1", PROFILE_PADDING)
      .attr("y1", BOTTOM_BAR_POSITION_TOP)
      .attr("x2", PROFILE_WIDTH + PROFILE_PADDING)
      .attr("y2", BOTTOM_BAR_POSITION_TOP)
    axisGroup.append("line")
      .attr("class", AXIS_LINE_CLASS)
      .attr("x1", PROFILE_PADDING)
      .attr("y1", BOTTOM_BAR_POSITION_TOP)
      .attr("x2", PROFILE_PADDING)
      .attr("y2", TOP_BAR_POSITION_TOP + TOP_BAR_PADDING)
    const xUnit = this.points[this.points.length - 1].x > 999 ? "km" : "m"
    const yUnit = this.gpx?.get_elevation_max() > 999 ? "km" : "m";
    const yAxisLabel = axisGroup.append("text").text(`${this.translocoService.translate("app.common.waypoint.elevation")} (${yUnit})`)
      .attr("class", AXIS_LABEL_CLASS).attr("x", 0).attr("y", 0)
    yAxisLabel.attr("transform", `rotate(-90,0,0) translate(-${yAxisLabel.node().getBoundingClientRect().width + TOP_BAR_PADDING},${PROFILE_PADDING / 2})`)
    const xAxisLabel = axisGroup.append("text").text(`${this.translocoService.translate("app.common.waypoint.distance")} (${xUnit})`)
      .attr("class", AXIS_LABEL_CLASS)
    xAxisLabel.attr("x", (PROFILE_WIDTH + PROFILE_PADDING - xAxisLabel.node().getBoundingClientRect().width))
    const distanceGroup = g.append("g")
    const nameGroup = g.append("g")
    let previousNameElement;

    for (let point of this.controlPointsList) {
      // // Append distance label
      distanceGroup.append("text")
        .attr("id", DISTANCE_ID + point.id)
        .attr("class", CP_DISTANCE_LABEL_CLASS)
        .text(raceLengthFormat(point.distanceFromStart, this.translocoService.getActiveLang(), xUnit, false) ?? "")
        .attr("x", scaleX(point.distanceFromStart))
        .attr("y", BOTTOM_BAR_POSITION_TOP);
      // Append name label
      const nameLabel = nameGroup.append("text")
        .attr("id", NAME_ID + point.id)
        .attr("class", CP_NAME_LABEL_CLASS)
        .text(this.getPointName(point).toUpperCase())
        .attr("x", scaleX(point.distanceFromStart))
        .attr("y", BOTTOM_BAR_POSITION_TOP);
      if (previousNameElement) {
        const prevNode = previousNameElement.node().getBoundingClientRect();
        const currNode = nameLabel.node().getBoundingClientRect();
        if (prevNode.x + prevNode.width > currNode.x && prevNode.y === currNode.y) {
          nameLabel.attr("y", BOTTOM_BAR_POSITION_TOP + currNode.height)
        }
      }
      previousNameElement = nameLabel;
    }
    const distanceGroupPosition = (distanceGroup ? distanceGroup.node().getBoundingClientRect().height : 0) + DISTANCE_GROUP_PADDING_TOP;
    const nameGroupPosition = (previousNameElement ? previousNameElement.node().getBoundingClientRect().height : 0) + NAME_GROUP_PADDING_TOP;
    distanceGroup.attr("transform", `translate(-0,${distanceGroupPosition})`)
    nameGroup.attr("transform", `translate(-0,${nameGroupPosition})`)
    xAxisLabel.attr("y", BOTTOM_BAR_POSITION_TOP + 6 + distanceGroupPosition + nameGroupPosition + xAxisLabel.node().getBoundingClientRect().height);

  }

  drawCheckpoints(g: any, scaleX: any, scaleY: any) {
    for (let i = 0; i < this.controlPointsList.length; i++) {
      const point = this.controlPointsList[i];
      if (point.waypointTypeId !== WaypointType.FINISH_WAYPOINT && point.waypointTypeId !== WaypointType.START_WAYPOINT) {
        g.append("line")
          .attr("id", LINE_ID + point.id)
          .attr("class", CP_LINE_CLASS)
          .attr("x1", scaleX(point.distanceFromStart))
          .attr("y1", TOP_BAR_PADDING)
          .attr("x2", scaleX(point.distanceFromStart))
          .attr("y2", scaleY(0))
        this.drawCheckPoint(g, point, scaleX(point.distanceFromStart,), TOP_BAR_PADDING, i);
      }
    }
  }

  createIcon(g: any, path: string, x: number, y: number, width: number): void {
    d3.xml(path).then((xml: XMLDocument) => {
      const importedNode = this.document.importNode(xml.documentElement, true);
      const icon = g
        .append(() => importedNode)
        .attr('x', x)
        .attr('y', y)
        .attr('height', `${width}px`)
        .attr('width', `${width}px`)
      icon.selectAll('path').attr('fill', 'white');
    });

  }

  drawCheckPoint(g: any, waypoint: Waypoint, x: number, y: number, index: number) {
    const checkPointMarkerLabel = `${this.translocoService.translate("app.common.waypoint.short")}${waypoint.ordinalNumber ?? ""}`
    const height = MARKER_RADIUS * (((waypoint.markerTypes?.length ?? 0) > 1) ? 3 : 2)
    if (waypoint.markerTypes?.length ?? 0 > 0) {
      const services = g.append("g")
      services.append("rect").attr("height", height).attr("width", 2 * MARKER_RADIUS).attr("x", x - MARKER_RADIUS).attr("y", y).attr("class", "race-profile fill-primary")
      services.append("circle")
        .attr("cx", x)
        .attr("cy", y + height)
        .attr("r", MARKER_RADIUS)
        .attr("class", "race-profile fill-primary")
      this.createIcon(services, this.waypointTypeIcons[waypoint.markerTypes![0]], x - MARKER_RADIUS / 2, y + MARKER_RADIUS * 1.3, MARKER_RADIUS)
      if ((waypoint.markerTypes?.length ?? 0) > 1) {
        services.append("text").text("+" + (waypoint.markerTypes!.length - 1).toString()).attr("x", x).attr("y", y + MARKER_RADIUS * 3.3).attr("class", CP_MARKER_SERVICE_NUMBER)
        services.append("rect").attr("x", x - MARKER_RADIUS / 2).attr("width", MARKER_RADIUS).attr("height", 1).attr("y", y + 3.5 * MARKER_RADIUS).attr("class", CP_MARKER_SERVICE_NUMBER)
      }
      services.on("mouseenter", () =>
        this.handleServicesHover(g, waypoint.markerTypes!, x, y, 2.5 * MARKER_RADIUS, 1.5 * MARKER_RADIUS)
      );
      g.on("mouseleave", () =>
        this.removeElementById(g, SERVICES_TOOLTIP_ID)
      );
    }

    const button = g.append("g")
    button.append("circle")
      .attr("cx", x)
      .attr("cy", y)
      .attr("r", MARKER_RADIUS)
      .attr("class", CP_MARKER_CIRCLE)
    button.append("text").attr("x", x).attr("y", y).text(checkPointMarkerLabel).attr("class", CIRCLE_TEXT).attr("dy", 7)
    if (waypoint.id) {
      button.on("click", this.openRaceProfileModalModal(waypoint, this.controlPointsList[index - 1], this.elevationStats.get(waypoint.id)))
    }

  }

  openRaceProfileModalModal(current: Waypoint, previous: Waypoint, elevationStats: ElevationStats | undefined) {
    return () => {
      const modalRef = this.modalService.open(RaceProfileModalComponent, {
        centered: true,
        size: "lg"
      });
      modalRef.componentInstance.waypoint = current;
      modalRef.componentInstance.previousWaypoint = previous;
      modalRef.componentInstance.languages = this.languages;
      modalRef.componentInstance.elevationStats = elevationStats
      modalRef.result.then((_) => {
      }, () => {
      });
    }
  }


  handleServicesHover(g: any, markerTypes: number[], x: number, y: number, dx: number, dy: number) {
    // Remove hover elements
    this.removeElementById(g, SERVICES_TOOLTIP_ID)

    const toolTipText = markerTypes.map(type => this.translocoService.translate("app.common.waypoint.marker.types." + type));


    if (x > GRAPH_WIDTH / 2) {
      this.createToolTip(g, SERVICES_TOOLTIP_ID, 20, x - dx, y + dy,
        "BOTTOM_LEFT",
        SERVICES_TOOLTIP_CLASS, SERVICES_TOOLTIP_TEXT_CLASS,
        toolTipText, 0, 10).attr("class", SERVICES_TOOLTIP_BORDER)
    } else {
      this.createToolTip(g, SERVICES_TOOLTIP_ID, 20, x + dx, y + dy,
        "BOTTOM_RIGHT",
        SERVICES_TOOLTIP_CLASS,
        SERVICES_TOOLTIP_TEXT_CLASS,
        toolTipText, 0, 10).attr("class", SERVICES_TOOLTIP_BORDER)
    }
  }


  handleMouseMove(g: any, scaleX: any, scaleY: any) {
    return (event: MouseEvent, d: ChartPoint[]) => {
      // Remove hover elements
      this.removeElementById(g, AREA_TOOLTIP_ID)

      const mouseX = d3.pointer(event)[0]; // Get the x-coordinate of the mouse pointer
      const bisect = d3.bisector((d: ChartPoint) => d.x).left; // Create a bisector function based on x-coordinate
      const index = bisect(d, scaleX.invert(mouseX)); // Find the index of the nearest data point
      const nearestDataPoint = d[index]; // Get the nearest data point

      const toolTipText = [
        `${this.translocoService.translate("app.common.waypoint.elevation")} : ${raceLengthFormat(nearestDataPoint.y, this.translocoService.getActiveLang())}`,
        `${this.translocoService.translate("app.common.waypoint.distance")} : ${raceLengthFormat(nearestDataPoint.x, this.translocoService.getActiveLang())}`
      ]
      if (mouseX > GRAPH_WIDTH / 2) {
        this.createToolTip(g, AREA_TOOLTIP_ID, 20, mouseX, scaleY(nearestDataPoint.y), "BOTTOM_LEFT", AREA_TOOLTIP_CLASS, AREA_TOOLTIP_TEXT_CLASS, toolTipText, 30, 10).attr("opacity", 0.85);
      } else {
        this.createToolTip(g, AREA_TOOLTIP_ID, 20, mouseX, scaleY(nearestDataPoint.y), "BOTTOM_RIGHT", AREA_TOOLTIP_CLASS, AREA_TOOLTIP_TEXT_CLASS, toolTipText, 30, 10).attr("opacity", 0.85)
      }

      // Hrizontal line from y=0 to the y-coordinate of the nearest data point
      g.append("line")
        .attr("id", AREA_TOOLTIP_ID)
        .attr("class", AREA_HOVER_LINE_CLASS)
        .attr("x1", scaleX(nearestDataPoint.x))
        .attr("y1", scaleY(nearestDataPoint.y))
        .attr("x2", scaleX(nearestDataPoint.x))
        .attr("y2", scaleY(0))

      // Height marker circle at the y-coordinate (height) of the nearest data point
      g.append("circle")
        .attr("id", AREA_TOOLTIP_ID)
        .attr("class", HEIGHT_MARKER)
        .attr("cx", scaleX(nearestDataPoint.x))
        .attr("cy", scaleY(nearestDataPoint.y))
        .attr("r", 5)
    }
  }

  removeElementById(g: any, id: string) {
    g.selectAll(`#${id}`).remove();
  }


  createUpperLine(g: any, scaleX: any, scaleY: any) {
    const line = d3.line<ChartPoint>()
      .x(d => scaleX(d.x))
      .y(d => scaleY(d.y));

    return g.append("path")
      .datum(this.points)
      .attr("class", ELEVATION_LINE)
      .attr("d", line);
  }

  drawRaceProfile(svg: any) {
    const g = this.createSvgAndGroup(svg);
    this.svg = g;

    const scaleX = d3.scaleLinear([this.points[0].x, this.points[this.points.length - 1].x], [PROFILE_PADDING, GRAPH_WIDTH - PROFILE_PADDING]);
    const scaleY = d3.scaleLinear([this.gpx?.get_elevation_min(), this.gpx?.get_elevation_max()], [PROFILE_POSITION_BOTTOM, PROFILE_POSITION_TOP]);

    const areaPath = this.createAreaPath(g, scaleX, scaleY);
    this.createUpperLine(g, scaleX, scaleY);

    areaPath.on("mousemove", this.handleMouseMove(g, scaleX, scaleY));
    g.on("mouseleave", () => this.removeElementById(g, AREA_TOOLTIP_ID));
    this.drawCheckpoints(g.append("g"), scaleX, scaleY);
    this.drawAxis(g, scaleX, scaleY);

  }

  drawGraph() {
    if (this.svgElement?.nativeElement) {
      const svgHolder = d3.select(this.svgElement.nativeElement);
      this.drawRaceProfile(svgHolder);
    }
  }

  getPointName(point: Waypoint): string {
    return getLanguageValue(point.pointNameHr, point.pointNameEn, point.pointNameIt, point.pointNameSlo, point.pointNameSr, point.pointNameBs, this.translocoService.getActiveLang()) ?? "";
  }
}


